// A search box that opens /support/search (pages/Search.jsx) with what was typed. The top bar's
// box (layout/AppLayout.jsx) and the results page's own box are both this component, so
// the two submit the same way; what submitting decides is searchSubmission, in
// pages/search-results.js, where it is tested.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { stripSupportBase } from '../support-path.js';
import { MAX_QUERY_LENGTH, readSearch, searchSubmission } from '../pages/search-results.js';

/**
 * Enter opens the first page of /support/search for what was typed; a blank box goes nowhere.
 * When that is exactly the address already showing, it replaces the history entry
 * rather than adding one, and the page fetches again (searchSubmission says why).
 * searchHref replaces the characters the API refuses.
 *
 * What the box shows follows the URL, as the queue's own search box does
 * (components/tickets/TicketFilters.jsx): it is reset whenever the query it should show
 * changes -- the URL's q on /support/search, and empty anywhere else. So arriving at a search,
 * or Back to another one, puts that search's text in the box, and leaving /support/search
 * empties it. It is NOT reset by paging, where q stays the same, nor by moving between
 * two pages other than /support/search: text typed there and never submitted stays.
 */
export default function SearchForm({ id, className, inputClassName, label, children }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const shown = stripSupportBase(location.pathname) === '/search' ? readSearch(searchParams).q : '';
  const [draft, setDraft] = useState(shown);

  useEffect(() => { setDraft(shown); }, [shown]);

  function submit(event) {
    event.preventDefault();
    const target = searchSubmission(draft, `${location.pathname}${location.search}`);
    if (target) navigate(target.href, { replace: target.replace });
  }

  return (
    <form role="search" aria-label={label} onSubmit={submit} className={className}>
      <label htmlFor={id} className="sr-only">{label}</label>
      <input
        id={id}
        type="search"
        className={`field-input h-9 ${inputClassName ?? ''}`}
        placeholder="Search tickets"
        maxLength={MAX_QUERY_LENGTH}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
      {children}
    </form>
  );
}
